// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ChartOptionsBuilder.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import {
  get,
  omitBy,
  size,
  isNil,
  has,
  isArray,
  forEach,
  filter,
  map,
  mapValues,
  escape,
  set,
  getOr,
  isString,
  isObject,
  isEmpty,
  merge,
  mergeAll
} from 'lodash/fp';
import { isObservableArray } from 'mobx';
import { exportClass } from 'share/Izenda.ServiceProvider';

@exportClass('ChartOptionsBuilder')
export default class ChartOptionsBuilder {
  constructor(visualType: string, chartContainer: Object, chartData: any, chartOptions: any, fieldOptions: any) {
    this.visualType = visualType;
    this.chartOptions = chartOptions || {};
    this.chartData = chartData || [];
    this.fieldOptions = fieldOptions;
    this.chartContainer = chartContainer;

    // Set axis name base on chart data
    this.chartOptions.xAxis &&
      this.chartOptions.xAxis.forEach(axis => {
        axis && (axis.axisName = this.chartData[0] && this.chartData[0][0]);
      });
  }

  setOnClickXLabel(xLabelClickHandler) {
    this.chartOptions.onClickXLabel = xLabelClickHandler;
  }

  setOnClickYLabel(yLabelClickHandler) {
    this.chartOptions.onClickYLabel = yLabelClickHandler;
  }

  mapOptionValues(optionsMapping) {
    const { chartOptions, fieldOptions } = mapOptionValues(this.chartOptions, this.fieldOptions, optionsMapping);

    this.chartOptions = chartOptions;
    this.fieldOptions = fieldOptions;
  }

  build() {
    throw new Error('The build() method of ChartOptionsBuilder must be implementated appropriately.');
  }
}

/**
 * Map Izenda option values into Highchart option values
 * @param {Object} chartOptions Izenda chart options
 * @param {Object} fieldOptions Izenda field options
 */
function mapOptionValues(chartOptions, fieldOptions, optionsMapping) {
  const {
    commonOptions: commonOptionsMapping,
    optionsByType: optionByTypeMapping,
    axisOptions: axisOptionsMapping,
    thresholdOptions: thresholdOptionsMapping
  } = optionsMapping || {};
  const { commonOptions, optionByType, commonXYAxis, ...restChartOpts } = chartOptions || {};

  // firstly, Restructure axis option from commonXYAxis(izGridline, izLabelOrientation), then parse it to highchart option
  const xAxis = {
    izLabelOrientation: get('izLabelOrientationX', commonXYAxis),
    ...get('izGridLine.xAxis', commonXYAxis),
    ...getOr({}, 'xAxis', commonXYAxis)
  };
  const yAxis = {
    izLabelOrientation: get('izLabelOrientationY', commonXYAxis),
    ...get('izGridLine.yAxis', commonXYAxis),
    ...getOr({}, 'yAxis', commonXYAxis)
  };

  return {
    chartOptions: {
      commonOptions: parseOptions(commonOptions, commonOptionsMapping),
      optionByType: parseOptions(optionByType, optionByTypeMapping),
      commonXYAxis: {
        xAxis: parseOptions(xAxis, axisOptionsMapping),
        yAxis: parseOptions(yAxis, axisOptionsMapping),
        metricAllOption: parseOptions(get('metricAllOption', commonXYAxis), axisOptionsMapping),
        metricAllPlotLinesOption: parseOptions(
          getVisibleThresholdSetting(get('metricAllPlotLinesOption', commonXYAxis)),
          thresholdOptionsMapping
        ),
        metricAllDynamicThresholdOption: parseOptions(
          getVisibleThresholdSetting(get('allDynamicThresholdLine', commonXYAxis)),
          thresholdOptionsMapping
        )
      },
      ...restChartOpts
    },
    fieldOptions: mapValues(
      ({ chartOptions, axisOption, threshold, dynamicThresholds, ...restSerieOpts }) => ({
        chartOptions: parseOptions(chartOptions, optionByTypeMapping),
        axisOption: parseOptions(axisOption, axisOptionsMapping),
        threshold: parseOptions(getVisibleThresholdSetting(threshold), thresholdOptionsMapping),
        dynamicThresholds: parseOptions(getVisibleThresholdSetting(dynamicThresholds), thresholdOptionsMapping),
        ...restSerieOpts
      }),
      fieldOptions
    )
  };
}

/**
 * Get visible threshold setting
 * @param {Object} thresholdSettings
 */
function getVisibleThresholdSetting(thresholdSettings: any) {
  return filter(item => item.izVisibleThresholdsSetting && !!item.izValue, thresholdSettings);
}

/**
 * Parse options
 * @param {Object} objValue
 * @param {Object} objMapping
 */
function parseOptions(objValue: any, objMapping: any) {
  switch (true) {
    case !objValue || !objMapping:
      return objValue;
    case isString(objValue):
      return objMapping[objValue];
    case isArray(objValue) || isObservableArray(objValue):
      return map(item => parseOptions(item, objMapping), objValue);
    case isObject(objValue):
      {
        const libOptions = Object.keys(objValue).reduce((acc, prop) => {
          const mapping = objMapping[prop];
          return mapping && objValue[prop] != null
            ? merge(
              { [mapping.key]: mapping.source ? parseOptions(objValue[prop], mapping.source) : objValue[prop] },
              acc
            )
            : objValue[prop] != null
              ? merge({ [prop]: objValue[prop] }, acc)
              : acc;
        }, {});

        // Run thru the properties then break into hierarchical object.
        // For example "izBorder.style.font = value" to be { izBorder : { style : { font: value }}}
        return Object.keys(libOptions).reduce((acc, prop) => set(prop, libOptions[prop], acc), {});
      }
    default:
      throw new Error('Cannot recognize objValue type to parse');
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/charting/Izenda.ChartOptionsBuilder.js